/* =====================================================================
 * Tenant resolution for the askyourai app.
 *
 * Phase 3 ships a single tenant (`b2cManifest`), but the resolver
 * signature already accepts a tenant id so later phases (enterprise
 * scaffold, on-prem config overlay) can plug in without touching
 * every caller again.
 *
 * Selection order
 *   1. Explicit `VITE_TENANT_ID` build-time env (future B2B builds).
 *   2. Hard fallback to `askyourai` (the public SaaS tenant).
 *
 * Runtime overlays (e.g. `/config.json` for on-premise) are a
 * separate Phase 8 concern and intentionally NOT wired in here.
 * ===================================================================== */

import type { TenantManifest } from '@auw/tenant';
import { b2cManifest } from './b2c.manifest';

const TENANT_REGISTRY: Record<string, TenantManifest> = {
  askyourai: b2cManifest,
};

/**
 * Resolve the active tenant manifest for this build.
 *
 * Throws if `VITE_TENANT_ID` is set to an unknown id rather than
 * silently falling back — an unknown tenant is almost always a
 * misconfiguration we want surfaced early.
 */
export function resolveTenantManifest(): TenantManifest {
  const requested = import.meta.env.VITE_TENANT_ID as string | undefined;
  if (!requested) return b2cManifest;

  const found = TENANT_REGISTRY[requested];
  if (!found) {
    const known = Object.keys(TENANT_REGISTRY).join(', ');
    throw new Error(
      `[tenant] Unknown VITE_TENANT_ID="${requested}"; known tenants: ${known}`,
    );
  }
  return found;
}

export { b2cManifest };
